import React from 'react';
import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { formatCurrency } from '@src/scripts/util/currency';
import BarChart from '../charts/BarChart';
import CumulativeChart from '../charts/CumulativeChart';

// Minimal inline line icons, matching IndicationsTable.js's convention of
// raw <svg> primitives rather than an icon library — each tile/card gets
// one, mirroring the reference tool's (simuprint.vercel.app) corner icons.
const TrendIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 20 20"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.6"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<polyline points="2,15 7,9 11,13 18,4" />
		<polyline points="12,4 18,4 18,10" />
	</svg>
);

const WalletIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 20 20"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.6"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="2" y="5" width="16" height="11" rx="2" />
		<path d="M2 8.5h16" />
		<circle cx="14" cy="12" r="1.1" fill="currentColor" stroke="none" />
	</svg>
);

const ClockIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 20 20"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.6"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<circle cx="10" cy="10" r="7.5" />
		<path d="M10 6v4l3 2" />
	</svg>
);

// Formatting-only helper for the tiles' footnote copy below — never used for
// the headline values themselves, which always go through formatCurrency().
const money = ( value, currency, locale ) =>
	formatCurrency( value, currency, locale );

// "Recovers the investment in …" — same wording family for both the Net
// Profit tile's footnote and the Months-for-ROI tile's subtitle, since both
// are just different framings of the same monthsForRoi number (reference
// tool: "Recupera l'investimento in meno di 12 mesi." / "Ammortamento
// completo in meno di 1 anno").
const payoffPhrase = ( monthsForRoi ) => {
	if ( monthsForRoi === null ) {
		return __(
			'Not reached at current savings.',
			'sprintray-roi-calculator'
		);
	}

	if ( monthsForRoi <= 12 ) {
		return __(
			'Recovers the investment in under a year.',
			'sprintray-roi-calculator'
		);
	}

	return sprintf(
		/* translators: %d: number of months */
		__(
			'Recovers the investment in %d months.',
			'sprintray-roi-calculator'
		),
		monthsForRoi
	);
};

const ResultsStep = ( {
	annualSavings,
	netProfitYearOne,
	monthsForRoi,
	monthlySavings,
	devicePricePaid,
	productName,
	annualLabCost,
	annualInternalCost,
	cumulativeSavings,
	currency,
	locale,
} ) => (
	<div className="roi-calculator__step roi-calculator__step--results">
		<h3>{ __( 'Your results', 'sprintray-roi-calculator' ) }</h3>
		<div className="roi-calculator__results-grid">
			<div className="roi-calculator__result-tile roi-calculator__result-tile--filled">
				<div className="roi-calculator__result-head">
					<span className="roi-calculator__result-label">
						{ __( 'Annual Savings', 'sprintray-roi-calculator' ) }
					</span>
					<span className="roi-calculator__result-icon">
						<TrendIcon />
					</span>
				</div>
				<span className="roi-calculator__result-value">
					{ money( annualSavings, currency, locale ) }
				</span>
				<span className="roi-calculator__result-sub">
					{ __(
						'vs. outsourcing to the lab',
						'sprintray-roi-calculator'
					) }
				</span>
				<div className="roi-calculator__result-divider" />
				<span className="roi-calculator__result-footnote">
					{ sprintf(
						/* translators: %s: formatted monthly savings amount */
						__(
							"That's %s/month saved on prosthetic costs.",
							'sprintray-roi-calculator'
						),
						money( monthlySavings, currency, locale )
					) }
				</span>
			</div>

			<div className="roi-calculator__result-tile roi-calculator__result-tile--accent">
				<div className="roi-calculator__result-head">
					<span className="roi-calculator__result-label">
						{ __(
							'Net Profit — Year 1',
							'sprintray-roi-calculator'
						) }
					</span>
					<span className="roi-calculator__result-icon">
						<WalletIcon />
					</span>
				</div>
				<span className="roi-calculator__result-value">
					{ money( netProfitYearOne, currency, locale ) }
				</span>
				<span className="roi-calculator__result-sub">
					{ sprintf(
						/* translators: %s: formatted device price */
						__(
							'Year-1 savings minus device price (%s)',
							'sprintray-roi-calculator'
						),
						money( devicePricePaid, currency, locale )
					) }
				</span>
				<div className="roi-calculator__result-divider" />
				<span className="roi-calculator__result-footnote">
					{ payoffPhrase( monthsForRoi ) }
				</span>
			</div>

			<div className="roi-calculator__result-tile">
				<div className="roi-calculator__result-head">
					<span className="roi-calculator__result-label">
						{ __(
							'Months for Return on Investment',
							'sprintray-roi-calculator'
						) }
					</span>
					<span className="roi-calculator__result-icon">
						<ClockIcon />
					</span>
				</div>
				<span className="roi-calculator__result-value roi-calculator__result-value--neutral">
					{ monthsForRoi === null
						? __( 'N/A', 'sprintray-roi-calculator' )
						: sprintf(
								/* translators: %d: number of months */
								__( '%d months', 'sprintray-roi-calculator' ),
								monthsForRoi
						  ) }
				</span>
				<span className="roi-calculator__result-sub">
					{ payoffPhrase( monthsForRoi ) }
				</span>
				<div className="roi-calculator__result-divider" />
				<span className="roi-calculator__result-footnote">
					{ monthsForRoi === null
						? __(
								'No payback under current inputs.',
								'sprintray-roi-calculator'
						  )
						: sprintf(
								/* translators: 1: formatted monthly savings, 2: number of months, 3: formatted device price, 4: product name */
								__(
									'%1$s/month × %2$d = %3$s (%4$s)',
									'sprintray-roi-calculator'
								),
								money( monthlySavings, currency, locale ),
								monthsForRoi,
								money( devicePricePaid, currency, locale ),
								productName
						  ) }
				</span>
			</div>
		</div>

		<div className="roi-calculator__charts-grid">
			<div className="roi-calculator__chart-card">
				<div className="roi-calculator__chart-card-head">
					<div>
						<h4>
							{ __(
								'Annual Cost — Outsourced vs. In-house',
								'sprintray-roi-calculator'
							) }
						</h4>
						<p className="roi-calculator__chart-card-sub">
							{ __(
								'Sum of all active indications.',
								'sprintray-roi-calculator'
							) }
						</p>
					</div>
				</div>
				<BarChart
					bars={ [
						{
							label: __(
								'Outsourced to lab',
								'sprintray-roi-calculator'
							),
							value: annualLabCost,
							variant: 'muted',
						},
						{
							label: __(
								'Produced in-house',
								'sprintray-roi-calculator'
							),
							value: annualInternalCost,
							variant: 'primary',
						},
					] }
					categoryLabel={ __( 'Annual', 'sprintray-roi-calculator' ) }
					currency={ currency }
					locale={ locale }
				/>
			</div>
			<div className="roi-calculator__chart-card">
				<div className="roi-calculator__chart-card-head">
					<div>
						<h4>
							{ __( 'Payoff Curve', 'sprintray-roi-calculator' ) }
						</h4>
						<p className="roi-calculator__chart-card-sub">
							{ __(
								'Cumulative savings vs. equipment price.',
								'sprintray-roi-calculator'
							) }
						</p>
					</div>
				</div>
				<CumulativeChart
					data={ cumulativeSavings }
					breakevenMonth={ monthsForRoi }
					currency={ currency }
					locale={ locale }
				/>
				{ monthsForRoi !== null && (
					<p className="roi-calculator__step-hint">
						{ __(
							'Breakeven reached at month',
							'sprintray-roi-calculator'
						) }{ ' ' }
						{ monthsForRoi } —{ ' ' }
						{ __(
							'pure profit afterwards.',
							'sprintray-roi-calculator'
						) }
					</p>
				) }
			</div>
		</div>
	</div>
);

ResultsStep.propTypes = {
	annualSavings: PropTypes.number,
	netProfitYearOne: PropTypes.number,
	monthsForRoi: PropTypes.number,
	monthlySavings: PropTypes.number,
	devicePricePaid: PropTypes.number,
	productName: PropTypes.string,
	annualLabCost: PropTypes.number,
	annualInternalCost: PropTypes.number,
	cumulativeSavings: PropTypes.array,
	currency: PropTypes.string,
	locale: PropTypes.string,
};

export default ResultsStep;
